import { useContext, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

import RootContext from '../context/RootContext';
import PrivateRoute from './PrivateRoute';
import GuestRoute from './GuestRoute';
import PreLoader from '../Loader/PreLoader';

// pages
import Login from '../pages/Login/Login';
import SemesterGrade from '../pages/Result/SemesterGrade';
import HostelBills from '../pages/Finance/HostelBills';
import NonAcademicFee from '../pages/Finance/NonAcademicFee';
import NonAcademicFeeTnxList from '../pages/Finance/NonAcademicFeeTnxList';


// lazy-loaded components
const FrontendLayout = lazy(() => import('./FrontendLayout'));
const PDFView = lazy(() => import('../pages/PDFView'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const ChangePassword = lazy(() => import('../pages/Setting/ChangePassword'));
const ChangeProfile = lazy(() => import('../pages/Setting/ChangeProfile'));
const ViewProfile = lazy(() => import('../pages/Setting/ViewProfile'));
const EnrolledCourses = lazy(() => import('../pages/Academic/EnrolledCourses'));
const Attendance = lazy(() => import('../pages/Academic/Attendance'));
const AdmitCard = lazy(() => import('../pages/Academic/AdmitCard'));
const TutionFee = lazy(() => import('../pages/Finance/TutionFee'));
const GeneratePaySlip = lazy(() => import('../pages/Finance/GeneratePaySlip'));
const TransactionTracking = lazy(() => import('../pages/Finance/TransactionTracking'));
const StudentLedger = lazy(() => import('../pages/Finance/StudentLedger'));
const CurrentAssessmentResult = lazy(() => import('../pages/Finance/CurrentAssessmentResult'));

const Error404 = lazy(() => import('../errors/Error404'));

const AppRoutes = () => {
  const { loading, setLoading } = useContext(RootContext);
  const location = useLocation();

  // stop preloader
  useEffect(() => {
    setLoading(false);
  }, [setLoading]);

  // disable right-click on all pages except login
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (location.pathname !== '/') { // assuming '/' is login page
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [location.pathname]);

  return loading ? (
    <PreLoader />
  ) : (
    <Suspense fallback={<PreLoader />}>
      <Routes>
        {/* Login page */}
        <Route index element={<GuestRoute><Login /></GuestRoute>} />

        {/* Protected routes */}
        <Route path="/*" element={<PrivateRoute><FrontendLayout /></PrivateRoute>}>
          {/* Settings */}
          <Route path="setting">
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="change-profile" element={<ChangeProfile />} />
            <Route path="view-profile" element={<ViewProfile />} />
          </Route>

          {/* Dashboard */}
          <Route path="dashboard" element={<Dashboard />} />

          {/* Academic */}
          <Route path="academic">
            <Route path="enrolled-courses" element={<EnrolledCourses />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="admit-card" element={<AdmitCard />} />
          </Route>

          {/* Finance */}
          <Route path="finance">
            <Route path="tution-fee" element={<TutionFee />} />
            <Route path="payment">
              <Route path="generate-pay-slip" element={<GeneratePaySlip />} />
              <Route path="transaction-logs" element={<TransactionTracking />} />
              <Route path="non-academic-tnx-list" element={<NonAcademicFeeTnxList />} />
              <Route path="non-academic" element={<NonAcademicFee />} />
              <Route path="hostel-bills" element={<HostelBills />} />
            </Route>
            <Route path="student-ledger" element={<StudentLedger />} />
          </Route>

          {/* Result */}
          <Route path="result">
            <Route path="semester-grade" element={<SemesterGrade />} />
            <Route path="current-assessment-result" element={<CurrentAssessmentResult />} />
          </Route>

          {/* PDF View */}
          <Route path="view-pdf/:id" element={<PDFView />} />

          {/* 404 */}
          <Route path="*" element={<Error404 />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
